import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { SmilePlus } from 'lucide-react'

export interface Reaction {
  id: string
  team_message_id: string
  user_id: string
  emoji: string
}

// Lean fixed v1 set (no emoji-picker dependency).
const EMOJI_SET = ['👍', '❤️', '😂', '🎉', '👀', '✅']

interface Props {
  messageId: string
  conversationId: string
  reactions: Reaction[]          // this message's reactions (from the parent's grouped query)
  currentUserId: string | null
}

export default function MessageReactions({ messageId, conversationId, reactions, currentUserId }: Props) {
  const [showPicker, setShowPicker] = useState(false)

  // Group by emoji → count + whether the current user is among the reactors.
  const groups: Record<string, { count: number; mine: boolean }> = {}
  for (const r of reactions) {
    const g = groups[r.emoji] || { count: 0, mine: false }
    g.count++
    if (r.user_id === currentUserId) g.mine = true
    groups[r.emoji] = g
  }

  const toggle = async (emoji: string) => {
    if (!currentUserId) return
    setShowPicker(false)
    if (groups[emoji]?.mine) {
      // toggle off — remove my reaction (RLS: own only)
      await supabase.from('message_reactions').delete()
        .eq('team_message_id', messageId).eq('user_id', currentUserId).eq('emoji', emoji)
    } else {
      const { error } = await (supabase as any).from('message_reactions')
        .insert({ team_message_id: messageId, team_conversation_id: conversationId, user_id: currentUserId, emoji })
      // a raced double-add hits the UNIQUE constraint → already reacted, ignore
      if (error && !/duplicate|unique/i.test(error.message || '')) console.warn('[reactions]', error.message)
    }
    // realtime invalidation in the parent refreshes the pills; no manual refetch here
  }

  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
      {Object.entries(groups).map(([emoji, g]) => (
        <button key={emoji} type="button" onClick={() => toggle(emoji)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 100, fontSize: 12, cursor: 'pointer',
            background: g.mine ? 'var(--accent-dim)' : 'var(--hover-bg)',
            border: `1px solid ${g.mine ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
            color: g.mine ? 'var(--accent-text)' : 'var(--ink-secondary)' }}>
          <span>{emoji}</span><span style={{ fontWeight: 600 }}>{g.count}</span>
        </button>
      ))}
      <button type="button" onClick={() => setShowPicker(v => !v)} aria-label="Add reaction"
        style={{ display: 'flex', alignItems: 'center', padding: '3px 7px', borderRadius: 100, background: 'none', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)' }}>
        <SmilePlus style={{ width: 13, height: 13 }} />
      </button>
      {showPicker && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, display: 'flex', gap: 2, padding: 4, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 10, zIndex: 10 }}>
          {EMOJI_SET.map(e => (
            <button key={e} type="button" onClick={() => toggle(e)}
              style={{ fontSize: 17, padding: '2px 5px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6, lineHeight: 1 }}>{e}</button>
          ))}
        </div>
      )}
    </div>
  )
}
